import * as eff from 'redux-saga/effects';
import * as Sentry from '@sentry/browser';
import mixpanel from 'mixpanel-browser';
import {
  DateTime,
  Interval,
} from 'luxon';

import * as actions from 'chronos-actions';
import * as selectors from 'chronos-selectors';
import config from 'chronos-config';
import {
  jiraApi,
  chronosApi,
} from 'chronos-api';

import {
  fetchProfile,
} from './profile';
import {
  fetchFilterResourcesById,
} from './filters';
import {
  throwError,
} from './helpers';

export default function* initialize() {
  try {
    yield eff.put(actions.setUiState({
      initializeInProcess: true,
    }));
    if (document.querySelector('meta[name="token"]')) {
      const jwtToken = document.querySelector('meta[name="token"]').getAttribute('content');
      chronosApi.setJWT(jwtToken);
    }
    const rootEl = window.document.getElementById('app');
    if (rootEl?.dataset?.jiratype === 'server') {
      jiraApi.setBaseUrl(
        '/rest/api/2',
        window.AJS.params.baseURL,
      );
      yield eff.put(actions.setUiState({
        jiraType: 'server',
      }));
    }
    yield eff.call(fetchProfile);
    const profile = yield eff.select(selectors.getProfile);
    if (
      window.location.hostname === 'reddo.me'
      || !profile.userData
    ) {
      Sentry.withScope((scope) => {
        Object.keys(profile).forEach((key) => {
          scope.setExtra(key, profile[key]);
        });
        Sentry.captureMessage('Check profile');
      });
    }
    const jiraUrl = profile.userData.self.split('/rest')[0];
    Sentry.configureScope((scope) => {
      scope.setTag('jiraUrl', jiraUrl);
      scope.setUser({
        id: (
          profile.userData.accountId
          || profile.userData.key
        ),
        email: profile.userData.emailAddress,
        displayName: profile.userData.displayName,
      });
    });
    if (config.useMixpanel) {
      mixpanel.identify((`${jiraUrl} - ${profile.userData.emailAddress}`));
      const mixPanelProfile = {
        id: (
          profile.userData.accountId
          || profile.userData.key
        ),
        $email: profile.userData.emailAddress,
        $name: profile.userData.displayName,
        jiraUrl,
      };
      mixpanel.people.set(mixPanelProfile);
      mixpanel.track(
        'Open plugin',
        mixPanelProfile,
      );
    }
    yield eff.put(actions.setUiState({
      jiraUrl,
    }));
    const chronosFilters = localStorage.getItem('chronosFilters');
    if (chronosFilters) {
      const filters = JSON.parse(chronosFilters)[profile.userData.self] || {};
      yield eff.call(
        fetchFilterResourcesById,
        {
          checkFilters: filters,
        },
      );
      yield eff.put(actions.setFilters(filters));
    }
    const contextView = localStorage.getItem('chronosContextView');
    if (contextView) {
      yield eff.put(actions.setUiState({
        contextView,
      }));
    }
    const showChronosGuide = localStorage.getItem('showChronosGuide');
    if (showChronosGuide === null) {
      yield eff.put(actions.setUiState({
        showGuide: true,
      }));
    }
    const chronosCalendarDate = localStorage.getItem('chronosCalendarDate');
    if (
      chronosCalendarDate
      && (
        [
          'currentMonth',
          'currentWeek',
          'currentDay',
        ].includes(chronosCalendarDate)
      )) {
      if (chronosCalendarDate === 'currentMonth') {
        yield eff.put(actions.setUiState({
          calendarDate: (
            Interval.fromDateTimes(
              DateTime.local().startOf('month'),
              DateTime.local().endOf('month'),
            )
          ),
        }));
      }
      if (chronosCalendarDate === 'currentWeek') {
        yield eff.put(actions.setUiState({
          calendarDate: (
            Interval.fromDateTimes(
              DateTime.local().startOf('week').minus({ days: 1 }),
              DateTime.local().endOf('week').minus({ days: 1 }),
            )
          ),
        }));
      }
      if (chronosCalendarDate === 'currentDay') {
        yield eff.put(actions.setUiState({
          calendarDate: (
            Interval.fromDateTimes(
              DateTime.local(),
              DateTime.local(),
            )
          ),
        }));
      }
    } else if (chronosCalendarDate) {
      yield eff.put(actions.setUiState({
        calendarDate: (
          Interval.fromISO(chronosCalendarDate)
        ),
      }));
    }
    yield eff.put(actions.refreshIssuesRequest({
      debouncing: false,
    }));
  } catch (err) {
    throwError(err);
  } finally {
    yield eff.put(actions.setUiState({
      initializeInProcess: false,
    }));
  }
}
